import { useState } from 'react';
import type {
  Idea, ReviewQueue, ReviewStaleIdea, ReviewConnection, ReviewRecurringIdea, ReviewCodeSuggestion, ReviewSuggestedIdea,
  ReviewCodeLinkSuggestion, ReviewStalledIdea,
} from '../api';
import * as api from '../api';
import { dismissDetections, mergeDuplicateGroup } from './DuplicatesView';
import { detectionsForMember, groupDuplicates, reviewCounts, type DuplicateGroupMember } from '../utils/duplicate-groups';
import { buildClaudePrompt, buildTriagePrompt, claudeUrl, loadClaudeTarget } from '../utils/open-in-claude';
import { Motif, currentTheme } from '../brand/Brand';
import { Icon } from '../icons';
import { RecurrenceBadge, recurrenceText } from './RecurrenceBadge';
import { CatchUp } from './CatchUp';

// Weekly triage (P3-07, round 2 Triage board): ideas suggested from coding
// sessions (the Inbox, P5-04), status changes GitHub suggests (P5-07), possible duplicates side by side, ideas that have gone quiet, new
// connections, and a hand-off to Claude for the thinking parts (P2-06).
// Mirrors the weekly-triage skill (P2-03).

const STATUS_LABEL: Record<string, string> = {
  captured: 'Captured', exploring: 'Exploring', building: 'Building', shipped: 'Shipped', shelved: 'Shelved',
};

const ISSUE_URL = /^https:\/\/github\.com\/([^/]+\/[^/]+)\/issues\/(\d+)$/;

function isIssueUrl(url: string): boolean {
  return ISSUE_URL.test(url);
}

/** https://github.com/o/r/issues/7 → "o/r#7" */
function issueRef(url: string): string {
  const m = ISSUE_URL.exec(url);
  return m ? `${m[1]}#${m[2]}` : url;
}

function snippet(body: string | null, max = 160): string | null {
  if (!body) return null;
  const flat = body.replace(/[#*_`>]+/g, '').replace(/\s+/g, ' ').trim();
  return flat.length > max ? flat.slice(0, max) + '…' : flat;
}

function shortDate(iso: string): string {
  return new Date(iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function weekOf(): string {
  const d = new Date();
  d.setDate(d.getDate() - d.getDay());
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** A stale row that was acted on this session: what happened, and how to take it back. */
interface Resolved {
  result: string;
  undo?: () => Promise<unknown>;
}

function DupCard({ m, keeper }: { m: DuplicateGroupMember; keeper: boolean }) {
  return (
    <div className={`tri-dup-card${keeper ? ' tri-dup-keeper' : ''}`}>
      <span className="tri-meta">
        <span className="tri-num">#{m.number}</span>
        <span className="tri-dot" style={{ background: `var(--nr-status-${m.status})` }} aria-hidden="true" />
        {STATUS_LABEL[m.status] ?? m.status} · {shortDate(m.created_at)}
        {keeper && <span className="tri-keeps">Keeps</span>}
      </span>
      <span className="tri-dup-title">{m.title}</span>
      {snippet(m.body) && <span className="tri-dup-body">{snippet(m.body)}</span>}
    </div>
  );
}

export function ReviewView({
  queue,
  loading,
  ideas,
  onSelect,
  onChanged,
  done,
  onDone,
}: {
  queue: ReviewQueue | null;
  loading: boolean;
  /** Ideas loaded in the app, for the "turn one into a spec" picker. */
  ideas: Idea[];
  onSelect: (id: number) => void;
  onChanged: () => void;
  /** Keys (`dup-<detection id>`, `conn-<id>`, `stale-<id>`, `rec-<id>`, `code-<suggestion id>`, `sug-<idea id>`,
   *  `link-<suggestion id>`, `stall-<idea id>`) acted on this session. Kept in
   *  App so the sidebar badge drops them too; server state catches up via onChanged(). */
  done: ReadonlySet<string>;
  onDone: (keys: string[]) => void;
}) {
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resolved, setResolved] = useState<Map<number, Resolved>>(new Map());
  const [staleSnapshot, setStaleSnapshot] = useState<ReviewStaleIdea[]>([]);
  const [specId, setSpecId] = useState<number | ''>('');
  /** Items handled on this screen since it opened, for "n of m done". */
  const [acted, setActed] = useState(0);
  const doneNow = (keys: string[]) => {
    // "Keeps coming back" isn't in the total (reviewCounts), so it doesn't count as done either.
    if (!keys.every((k) => k.startsWith('rec-'))) setActed((a) => a + 1);
    onDone(keys);
  };
  const target = loadClaudeTarget();

  const act = async (key: string, doneKeys: string[], fn: () => Promise<unknown>) => {
    setBusyKey(key);
    setError(null);
    try {
      await fn();
      doneNow(doneKeys);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusyKey(null);
      // Refetch even after a failure: part of a group may have merged.
      onChanged();
    }
  };

  /** Act on a stale idea but keep its row on screen with the result and an Undo. */
  const resolveStale = (idea: ReviewStaleIdea, result: string, fn: () => Promise<unknown>, undo?: () => Promise<unknown>) => {
    setStaleSnapshot((prev) => (prev.some((s) => s.id === idea.id) ? prev : [...prev, idea]));
    void act(`stale-${idea.id}`, [`stale-${idea.id}`], async () => {
      await fn();
      setResolved((prev) => new Map(prev).set(idea.id, { result, undo }));
    });
  };

  const undoStale = async (id: number) => {
    const r = resolved.get(id);
    if (!r?.undo) return;
    setBusyKey(`stale-${id}`);
    try {
      await r.undo();
      const idea = staleSnapshot.find((s) => s.id === id);
      const back = idea ? STATUS_LABEL[idea.status] ?? idea.status : 'its old status';
      setResolved((prev) => new Map(prev).set(id, { result: `Undone · back to ${back}` }));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusyKey(null);
      onChanged();
    }
  };

  if (loading && !queue) {
    return (
      <div className="tri">
        <div className="empty-state"><div className="spinner" /><p>Building your review…</p></div>
      </div>
    );
  }

  const groups = groupDuplicates((queue?.duplicates ?? []).filter((d) => !done.has(`dup-${d.id}`)));
  const connections = (queue?.connections ?? []).filter((c) => !done.has(`conn-${c.id}`));
  // Once explored (or acted on as a stale row) an idea leaves "Keeps coming back".
  const recurring = (queue?.recurring ?? []).filter((r) => !done.has(`rec-${r.id}`) && !done.has(`stale-${r.id}`));
  const code = (queue?.code ?? []).filter((s) => !done.has(`code-${s.id}`));
  const suggested = (queue?.suggested ?? []).filter((s) => !done.has(`sug-${s.id}`));
  const codeLinks = (queue?.code_links ?? []).filter((s) => !done.has(`link-${s.id}`));
  const stalled = (queue?.stalled ?? []).filter((s) => !done.has(`stall-${s.id}`));
  // Rows acted on this session stay (with Undo) until the page is left.
  const liveStale = (queue?.stale ?? []).filter((s) => !done.has(`stale-${s.id}`));
  const stale = [...liveStale, ...staleSnapshot.filter((s) => resolved.has(s.id) && !liveStale.some((l) => l.id === s.id))];
  const counts = reviewCounts(queue, done);
  const doneCount = acted;
  const totalAtStart = counts.total + acted;
  const dupKeys = (ds: { id: number }[]) => ds.map((d) => `dup-${d.id}`);
  const specIdea = ideas.find((i) => i.id === specId);

  return (
    <div className="tri">
      <header className="tri-head">
        <div className="tri-head-text">
          <h1 className="tri-title">Triage</h1>
          <span className="tri-sub">Week of {weekOf()} · {doneCount} of {totalAtStart} done</span>
        </div>
        <a className="fr-btn" href={claudeUrl(buildTriagePrompt(), target)} target="_blank" rel="noopener noreferrer">
          <Icon name="chat" size={16} /> Triage with Claude
        </a>
      </header>

      {error && <div className="tri-error" role="alert">{error}</div>}

      <CatchUp onSelect={onSelect} onChanged={onChanged} />

      {counts.total === 0 && stale.length === 0 && (
        <div className="tri-clear">
          <Motif className="tri-clear-motif" width={480} height={140} theme={currentTheme()} seed={64} pitch={18} fx={0.5} fy={0.5} mask="band" id="tri-clear" />
          <h2>All caught up</h2>
          <p>New duplicates, connections and ideas that go quiet for {queue?.stale_threshold_days ?? 30}+ days will show up here.</p>
        </div>
      )}

      {suggested.length > 0 && (
        <section className="tri-section" aria-labelledby="tri-sug">
          <h2 className="tri-h2" id="tri-sug">
            {suggested.some((s) => s.source_url && isIssueUrl(s.source_url)) ? 'Suggested from sessions and GitHub' : 'Suggested from sessions'} <span className="tri-count">{suggested.length}</span></h2>
          <p className="tri-help">
            {suggested.some((s) => s.source_url && isIssueUrl(s.source_url))
              ? 'Ideas Claude parked while you worked, and issues people opened in your repos.'
              : 'Ideas Claude parked while you worked.'}
            {' '}Keep moves one into your library; drop archives it. Untouched suggestions are archived after 30 days.
          </p>
          <div className="tri-rows">
            {suggested.map((sug: ReviewSuggestedIdea) => {
              const key = `sug-${sug.id}`;
              const busy = busyKey === key;
              const repo = sug.source_url && isIssueUrl(sug.source_url)
                ? issueRef(sug.source_url)
                : sug.source_url?.replace(/^https?:\/\/(www\.)?github\.com\//, '');
              return (
                <div key={key} className="tri-row">
                  <button className="tri-row-main" onClick={() => onSelect(sug.id)}>
                    <span className="tri-row-title"><span className="tri-num">#{sug.number}</span>{sug.title}</span>
                    <span className="tri-meta">
                      {repo ? <>{repo} · </> : null}{shortDate(sug.created_at)}
                      {snippet(sug.body, 100) && <> · {snippet(sug.body, 100)}</>}
                    </span>
                  </button>
                  <span className="tri-row-actions">
                    <button className="fr-btn tri-sm" disabled={busy}
                      onClick={() => void act(key, [key], () => api.keepSuggestion(sug.id))}>
                      Keep
                    </button>
                    <button className="tri-link" disabled={busy}
                      onClick={() => void act(key, [key], () => api.archiveIdea(sug.id))}>
                      Drop
                    </button>
                  </span>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {code.length > 0 && (
        <section className="tri-section" aria-labelledby="tri-code">
          <h2 className="tri-h2" id="tri-code">From your code <span className="tri-count">{code.length}</span></h2>
          <p className="tri-help">Commits and pull requests that mention an idea (NR-42, idea #42, or a branch named idea/42-…).</p>
          <div className="tri-rows">
            {code.map((sug: ReviewCodeSuggestion) => {
              const key = `code-${sug.id}`;
              const busy = busyKey === key;
              const to = STATUS_LABEL[sug.value] ?? sug.value;
              return (
                <div key={key} className="tri-row">
                  <button className="tri-row-main" onClick={() => onSelect(sug.idea_id)}>
                    <span className="tri-row-title"><span className="tri-num">#{sug.number}</span>{sug.title}</span>
                    <span className="tri-meta">
                      {sug.reason && <span className="tri-recur">{sug.reason}</span>}
                      · {STATUS_LABEL[sug.status] ?? sug.status} → {to}?
                    </span>
                  </button>
                  <span className="tri-row-actions">
                    {sug.ref_url && (
                      <a className="tri-link" href={sug.ref_url} target="_blank" rel="noopener noreferrer">View on GitHub</a>
                    )}
                    <button className="fr-btn tri-sm" disabled={busy}
                      onClick={() => void act(key, [key], () => api.resolveCodeSuggestion(sug.id, 'accept'))}>
                      Mark {to.toLowerCase()}
                    </button>
                    <button className="tri-link" disabled={busy}
                      onClick={() => void act(key, [key], () => api.resolveCodeSuggestion(sug.id, 'dismiss'))}>
                      Not yet
                    </button>
                  </span>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {codeLinks.length > 0 && (
        <section className="tri-section" aria-labelledby="tri-links">
          <h2 className="tri-h2" id="tri-links">
            {codeLinks.some((s) => isIssueUrl(s.ref_url)) ? 'Pull requests and issues' : 'Pull requests'} that look like an idea <span className="tri-count">{codeLinks.length}</span>
          </h2>
          <p className="tri-help">These mention no idea, but read like one of yours. Nothing is linked until you say so, and nothing is posted to GitHub.</p>
          <div className="tri-rows">
            {codeLinks.map((sug: ReviewCodeLinkSuggestion) => {
              const key = `link-${sug.id}`;
              const busy = busyKey === key;
              return (
                <div key={key} className="tri-row">
                  <button className="tri-row-main" onClick={() => onSelect(sug.idea_id)}>
                    <span className="tri-row-title"><span className="tri-num">#{sug.number}</span>{sug.title}</span>
                    <span className="tri-meta">
                      {isIssueUrl(sug.ref_url) ? 'Issue ' : 'PR '}
                      <span className="tri-recur">{sug.reason ?? sug.ref_url}</span>
                      · {STATUS_LABEL[sug.status] ?? sug.status}
                      {sug.idea_issue_url && <> · already has {issueRef(sug.idea_issue_url)}: possibly the same issue twice</>}
                    </span>
                  </button>
                  <span className="tri-row-actions">
                    <a className="tri-link" href={sug.ref_url} target="_blank" rel="noopener noreferrer">View on GitHub</a>
                    <button className="fr-btn tri-sm" disabled={busy}
                      onClick={() => void act(key, [key], () => api.resolveCodeSuggestion(sug.id, 'accept'))}>
                      Link to #{sug.number}
                    </button>
                    <button className="tri-link" disabled={busy}
                      onClick={() => void act(key, [key], () => api.resolveCodeSuggestion(sug.id, 'dismiss'))}>
                      Not this one
                    </button>
                  </span>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {stalled.length > 0 && (
        <section className="tri-section" aria-labelledby="tri-stalled">
          <h2 className="tri-h2" id="tri-stalled">Stalled? <span className="tri-count">{stalled.length}</span></h2>
          <p className="tri-help">Building, but no commits in their repos for six weeks.</p>
          <div className="tri-rows">
            {stalled.map((idea: ReviewStalledIdea) => {
              const key = `stall-${idea.id}`;
              const busy = busyKey === key;
              return (
                <div key={key} className="tri-row">
                  <button className="tri-row-main" onClick={() => onSelect(idea.id)}>
                    <span className="tri-row-title"><span className="tri-num">#{idea.number}</span>{idea.title}</span>
                    <span className="tri-meta">
                      {idea.repos.join(', ')}
                      {' · '}{idea.last_commit_at ? `last commit ${shortDate(idea.last_commit_at)}` : 'no recent commits'}
                    </span>
                  </button>
                  <span className="tri-row-actions">
                    <button className="fr-btn tri-sm" disabled={busy}
                      onClick={() => void act(key, [key], () => api.keepStalledIdea(idea.id))}>
                      Still building
                    </button>
                    <button className="tri-link" disabled={busy}
                      onClick={() => void act(key, [key], () => api.updateIdea(idea.id, { status: 'shelved' }))}>
                      Shelve
                    </button>
                  </span>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {recurring.length > 0 && (
        <section className="tri-section" aria-labelledby="tri-recur">
          <h2 className="tri-h2" id="tri-recur">Keeps coming back <span className="tri-count">{recurring.length}</span></h2>
          <p className="tri-help">Ideas you keep returning to: duplicates, merges and new captures close to them. A good place to start.</p>
          <div className="tri-rows">
            {recurring.map((r: ReviewRecurringIdea) => {
              const key = `rec-${r.id}`;
              const busy = busyKey === key;
              return (
                <div key={key} className="tri-row">
                  <button className="tri-row-main" onClick={() => onSelect(r.id)}>
                    <span className="tri-row-title"><span className="tri-num">#{r.number}</span>{r.title}</span>
                    <span className="tri-meta">
                      <span className="tri-recur">{recurrenceText(r.recurrence, r.recurrence_since)}</span>
                      · {STATUS_LABEL[r.status] ?? r.status}
                    </span>
                  </button>
                  <span className="tri-row-actions">
                    <a className="fr-btn tri-sm" href={claudeUrl(buildClaudePrompt('spec', r), target)} target="_blank" rel="noopener noreferrer">
                      <Icon name="spec" size={14} /> Spec it
                    </a>
                    <button className="fr-btn tri-sm" disabled={busy}
                      onClick={() => void act(key, [key], () => api.updateIdea(r.id, { status: 'exploring' }))}>
                      Start exploring
                    </button>
                  </span>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {groups.length > 0 && (
        <section className="tri-section" aria-labelledby="tri-dups">
          <h2 className="tri-h2" id="tri-dups">Possible duplicates <span className="tri-count">{groups.length}</span></h2>
          {groups.map((group) => {
            const busy = busyKey === group.key;
            const pair = group.members.length === 2;
            return (
              <div key={group.key} className="tri-card">
                <p className="tri-help">
                  {Math.round(group.topScore * 100)}% similar. Merging keeps #{group.keeper.number} and folds the
                  {pair ? ' other’s' : ' others’'} notes and tags into it.
                </p>
                <div className={`tri-dup-grid${pair ? '' : ' tri-dup-grid-many'}`}>
                  {group.members.map((m) => (
                    <div key={m.id} className="tri-dup-cell">
                      <button className="tri-dup-open" onClick={() => onSelect(m.id)} aria-label={`Open #${m.number} ${m.title}`}>
                        <DupCard m={m} keeper={m.id === group.keeper.id} />
                      </button>
                      {!pair && m.id !== group.keeper.id && (
                        <button
                          className="tri-link"
                          disabled={busy}
                          onClick={() => {
                            const ds = detectionsForMember(group, m.id);
                            void act(group.key, dupKeys(ds), () => dismissDetections(ds));
                          }}
                        >
                          Not this one
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <div className="tri-actions">
                  <button className="fr-btn fr-btn-primary" disabled={busy}
                    onClick={() => void act(group.key, dupKeys(group.detections), () => mergeDuplicateGroup(group))}>
                    {pair ? `Merge into #${group.keeper.number}` : `Merge ${group.members.length - 1} into #${group.keeper.number}`}
                  </button>
                  <button className="fr-btn" disabled={busy}
                    onClick={() => void act(group.key, dupKeys(group.detections), () => dismissDetections(group.detections))}>
                    Keep both
                  </button>
                  <button className="tri-link" disabled={busy} onClick={() => doneNow(dupKeys(group.detections))}>
                    Not now
                  </button>
                </div>
              </div>
            );
          })}
        </section>
      )}

      {stale.length > 0 && (
        <section className="tri-section" aria-labelledby="tri-stale">
          <h2 className="tri-h2" id="tri-stale">Gone quiet <span className="tri-count">{liveStale.length}</span></h2>
          <p className="tri-help">Captured, untouched for {queue?.stale_threshold_days ?? 30} days or more.</p>
          <div className="tri-rows">
            {stale.map((idea) => {
              const key = `stale-${idea.id}`;
              const r = resolved.get(idea.id);
              const busy = busyKey === key;
              return (
                <div key={key} className={`tri-row${r ? ' tri-row-done' : ''}`}>
                  <button className="tri-row-main" onClick={() => onSelect(idea.id)}>
                    <span className="tri-row-title"><span className="tri-num">#{idea.number}</span>{idea.title}</span>
                    <span className="tri-meta">
                      {r ? r.result : `${STATUS_LABEL[idea.status] ?? idea.status} · quiet for ${idea.days_stale} days`}
                      {!r && <RecurrenceBadge count={idea.recurrence} />}
                    </span>
                  </button>
                  <span className="tri-row-actions">
                    {r ? (
                      r.undo && <button className="tri-link" disabled={busy} onClick={() => void undoStale(idea.id)}>Undo</button>
                    ) : (
                      <>
                        <button className="fr-btn tri-sm" disabled={busy}
                          onClick={() => resolveStale(idea, 'Moved to Exploring',
                            () => api.updateIdea(idea.id, { status: 'exploring' }),
                            () => api.updateIdea(idea.id, { status: idea.status }))}>
                          Start exploring
                        </button>
                        <button className="fr-btn tri-sm" disabled={busy}
                          onClick={() => resolveStale(idea, 'Shelved',
                            () => api.updateIdea(idea.id, { status: 'shelved' }),
                            () => api.updateIdea(idea.id, { status: idea.status }))}>
                          Shelve
                        </button>
                        <button className="tri-link" disabled={busy}
                          title="Still relevant: resets the quiet clock"
                          onClick={() => resolveStale(idea, 'Kept', () => api.keepIdea(idea.id))}>
                          Keep
                        </button>
                      </>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {connections.length > 0 && (
        <section className="tri-section" aria-labelledby="tri-links">
          <h2 className="tri-h2" id="tri-links">New connections <span className="tri-count">{connections.length}</span></h2>
          <p className="tri-help">Found this week. Removing one stops it coming back.</p>
          <div className="tri-rows">
            {connections.map((conn: ReviewConnection) => {
              const key = `conn-${conn.id}`;
              const busy = busyKey === key;
              return (
                <div key={key} className="tri-row">
                  <span className="tri-row-main tri-link-pair">
                    <button className="tri-pair" onClick={() => onSelect(conn.source_idea_id)}><span className="tri-num">#{conn.source_number}</span>{conn.source_title}</button>
                    <span className="tri-rel">{conn.relation_type}</span>
                    <button className="tri-pair" onClick={() => onSelect(conn.target_idea_id)}><span className="tri-num">#{conn.target_number}</span>{conn.target_title}</button>
                  </span>
                  <span className="tri-row-actions">
                    <button className="fr-btn tri-sm" disabled={busy} onClick={() => void act(key, [key], () => api.keepConnection(conn.id))}>Keep</button>
                    <button className="tri-link" disabled={busy} onClick={() => void act(key, [key], () => api.deleteRelation(conn.id))}>Remove</button>
                  </span>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <section className="tri-section tri-spec" aria-labelledby="tri-spec">
        <h2 className="tri-h2" id="tri-spec">Turn one idea into a spec</h2>
        <p className="tri-help">Claude reads the idea and its connections, asks a few questions, and saves the spec back as a linked idea.</p>
        <div className="tri-actions">
          <select className="tri-select" aria-label="Choose an idea" value={specId} onChange={(e) => setSpecId(e.target.value ? Number(e.target.value) : '')}>
            <option value="">Choose an idea</option>
            {ideas.filter((i) => i.status !== 'shipped' && i.status !== 'shelved').slice(0, 200).map((i) => (
              <option key={i.id} value={i.id}>#{i.number} {i.title.slice(0, 80)}</option>
            ))}
          </select>
          {specIdea ? (
            <a className="fr-btn fr-btn-primary" href={claudeUrl(buildClaudePrompt('spec', specIdea), target)} target="_blank" rel="noopener noreferrer">
              <Icon name="spec" size={16} /> Draft the spec with Claude
            </a>
          ) : (
            <button className="fr-btn fr-btn-primary" disabled>
              <Icon name="spec" size={16} /> Draft the spec with Claude
            </button>
          )}
        </div>
      </section>
    </div>
  );
}
